import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import Button from '@space-lib/components/button'
import Icon from '@space-lib/components/icon'
import Modal from '@space-lib/components/modal'
import ModalBody from '@space-lib/components/modalBody'
import ModalFooter from '@space-lib/components/modalFooter'
import ModalHeader from '@space-lib/components/modalHeader'
import { Actions, Entities, EventNames, RequestResults, ScreenNames } from '@space-lib/datahub'
import useTranslation from '@space-lib/hooks/useTranslation'
import type { CommunityProps, UserBlockedFollowers } from '@space-shell/common/services/communities'
import { queryClient } from '@space-shell/config/request'
import { usePutBlockedFollower } from '@space-shell/modules/communities/hooks/usePutBlockedFollower'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'
import clsx from 'clsx'

export type ConfirmationUnBanModalProps = {
  isOpen: boolean
  community: CommunityProps
  user: UserBlockedFollowers
  closeModal: () => void
}

const paddingModalStyle = '_px-5 _py-4'

const ConfirmationUnBanModal = ({
  isOpen,
  user,
  community,
  closeModal
}: ConfirmationUnBanModalProps) => {
  const { t } = useTranslation()
  const { mutate: putBlockedFollower, isLoading } = usePutBlockedFollower()
  const userName = user?.name || user.userName

  const { sendDataHubConsume } = useSendDataHubConsume()

  const handleClick = async () => {
    await putBlockedFollower(
      {
        communityId: community.id,
        idUser: user.id
      },
      {
        onSuccess() {
          queryClient.invalidateQueries({
            queryKey: ['getBlockedFollowers']
          })

          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: t('notifications.unban_member.success', { user: userName }),
            position: ToastPosition.BOTTOM_RIGHT
          })

          sendDataHubConsume({
            entity: Entities.BAN,
            action: Actions.UNBLOCK,
            event: {
              eventName: EventNames.COMMUNITY_CONFIG_UNBLOCK_USER,
              screenName: ScreenNames.UNBAN_VIEW
            }
          })

          closeModal()
        },
        onError(error) {
          toastEventDispatch({
            type: ToastType.DANGER,
            message: t('notifications.unban_member.error', { user: userName }),
            position: ToastPosition.BOTTOM_RIGHT
          })

          sendDataHubConsume({
            entity: Entities.BAN,
            action: Actions.UNBLOCK,
            event: {
              eventName: EventNames.COMMUNITY_CONFIG_UNBLOCK_USER,
              screenName: ScreenNames.UNBAN_VIEW,
              result: RequestResults.FAILURE,
              failureKind: error?.message
            }
          })
        }
      }
    )
  }

  return (
    <Modal show={isOpen} onClose={closeModal}>
      <ModalHeader className='_px-5 _pt-5 _pb-0 '>
        <div className='_pb-1 _d-flex _justify-content-between _align-items-center'>
          <h2 className='_m-0 _text-4 _text-gray-700 _mb-0'>{t('modal.unban.title')}</h2>

          <button
            className='_p-0 _d-flex _border-0 _bg-transparent _text-gray-400 _cursor-pointer'
            onClick={closeModal}>
            <span className='_sr-only'>close</span>
            <Icon icon={faXmark} className='_w-5 _h-5' size='2x' />
          </button>
        </div>
      </ModalHeader>

      <ModalBody className={paddingModalStyle}>
        <p>{t('modal.unban.description', { user: userName })}</p>
      </ModalBody>

      <ModalFooter className={clsx('_d-flex _justify-content-end', paddingModalStyle)}>
        <Button className='_mr-5' variation='default' onClick={closeModal}>
          {t('buttons.back')}
        </Button>

        <Button onClick={handleClick} loading={isLoading} disabled={isLoading}>
          {t('modal.unban.button_unban_member')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ConfirmationUnBanModal
